import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { OwnerProvider } from "./context/OwnerContext";
import { AuthProvider } from "./auth/AuthContext";
import { ToastProvider, ErrorBoundary, EmulatorBanner } from "./components/shared";
import { ProtectedRoute, AdminRoute, ToolRoute, AuthRoute } from "./auth/RouteGuards";
import { MainLayout } from "./layouts";
import { DebugProvider } from "./debug";
import { FullscreenProvider, BreadcrumbProvider } from "./contexts";
import DashboardLayout from "./layouts/DashboardLayout";
import Spinner from "./components/Spinner";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/styles/scss/global.scss";

// ── Lazily-loaded route components ───────────────────────────────────────────
// Each page becomes its own async chunk, fetched on first navigation, so the
// initial bundle no longer ships every page up front (incl. the 113KB
// MailshotDetailPage and the Google Maps libraries).

// Public
const RetiredFormPage = lazy(() => import("./pages/RetiredFormPage"));
const FullMapPage = lazy(() => import("./components/mapv2/FullMapPage"));

// Auth
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const AcceptInvitePage = lazy(() => import("./pages/auth/AcceptInvitePage"));

// User
const ProfilePage = lazy(() => import("./pages/user/ProfilePage"));
const ChangePasswordPage = lazy(() => import("./pages/user/ChangePasswordPage"));

// Dashboard
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const UserManagementPage = lazy(() => import("./pages/dashboard/UserManagementPage"));
const InviteUserPage = lazy(() => import("./pages/dashboard/InviteUserPage"));
const ConfigPage = lazy(() => import("./pages/dashboard/ConfigPage"));
const PropertiesPage = lazy(() => import("./pages/dashboard/PropertiesPage"));
// /dashboard/map embeds the new map (28 Sep 2026). The old UnifiedMap, and its
// property layer, which read 40,000+ properties per load via mapProperties, are retired.
const FullMap = lazy(() => import("./components/mapv2/FullMap"));

// Dashboard tools
const RedirectsPage = lazy(() => import("./pages/dashboard/tools/RedirectsPage"));
const PromotersPage = lazy(() => import("./pages/dashboard/tools/PromotersPage"));
const PromoterVisitDetailPage = lazy(() => import("./pages/dashboard/tools/PromoterVisitDetailPage"));
const BannerAdDetailPage = lazy(() => import("./pages/dashboard/tools/BannerAdDetailPage"));
const EngagementPage = lazy(() => import("./pages/dashboard/tools/engagement/EngagementPage"));
const AnalyticsPage = lazy(() => import("./pages/dashboard/tools/analytics/AnalyticsPage"));
const CampaignsPage = lazy(() => import("./pages/dashboard/tools/campaigns/CampaignsPage"));
const SupportPage = lazy(() => import("./pages/dashboard/tools/support/SupportPage"));
const SupportFullScreen = lazy(() => import("./pages/dashboard/tools/support/SupportFullScreen"));
// Development-only visual check of the Analytics pages from a local fixture. The constant
// condition lets webpack drop it — and its chunk — from production builds.
const AnalyticsPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./dev/AnalyticsPreview"))
  : null;
// Development-only look at the Support Settings tab, over a stand-in for its functions. Dropped from
// production builds the same way; a build that holds it is refused (scripts/checkDashboardBuild.js).
const SupportSettingsPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./dev/SupportSettingsPreview"))
  : null;
const SocialPage = lazy(() => import("./pages/dashboard/tools/social/SocialPage"));
const HealthPage = lazy(() => import("./pages/dashboard/tools/HealthPage"));
const EngagementDetailPage = lazy(() => import("./pages/dashboard/tools/engagement/EngagementDetailPage"));

// Admin
const BackupPage = lazy(() => import("./pages/admin/BackupPage"));
const SettingsPage = lazy(() => import("./pages/admin/SettingsPage"));

const App = () => {
  // Load console-only debug/migration helpers after mount so they don't bloat the
  // initial bundle or block first paint. They attach utilities to `window` for
  // manual use from the browser console.
  useEffect(() => {
    import("./utils/migrations");
    import("./utils/migrateScrapesToScraper");
    import("./utils/updateScraperIdForScrapes");
    import("./utils/debugScrapeStructure");
    import("./utils/debugScrapers");
    import("./utils/fixMissingScraperId");
    import("./utils/migrateScraperIdToCreatedBy");
    import("./utils/updateCreatedBy");
  }, []);

  return (
    <ErrorBoundary>
      <DebugProvider>
        <AuthProvider>
          <ToastProvider>
            <OwnerProvider>
              <FullscreenProvider>
                <BreadcrumbProvider>
                <EmulatorBanner />
                <Router>
                  <Suspense
                    fallback={
                      <div className="auth-loading">
                        <Spinner text="Loading…" />
                      </div>
                    }
                  >
                  <Routes>
                    {/* The public promoter sign-up form was here, at both addresses. It is RETIRED (EST-194, Oct 2026):
                        its submit had nowhere to arrive. The addresses stay and say so; nothing is asked or sent. */}
                    <Route
                      path="/promoter"
                      element={
                        <MainLayout>
                          <RetiredFormPage />
                        </MainLayout>
                      }
                    />
                    <Route
                      path="/form/:owner"
                      element={
                        <MainLayout>
                          <RetiredFormPage />
                        </MainLayout>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />
                    {/* The public promoter map is now /map (team PIN, or a Tools sign-in). It
                        called mapPromoters with an anonymous token — anyone could. */}
                    <Route path="/promoters" element={<Navigate to="/map" replace />} />
                    {/* /map: the full map behind a colleague sign-in or the team PIN;
                        /map?lat=&lng=&title= stays the open single-pin page HubSpot links to. */}
                    <Route path="/map" element={<FullMapPage />} />

                    {/* Auth Routes - only for non-authenticated users (uses AuthLayout internally) */}
                    <Route
                      path="/login"
                      element={
                        <AuthRoute>
                          <LoginPage />
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/forgot-password"
                      element={
                        <AuthRoute>
                          <ForgotPasswordPage />
                        </AuthRoute>
                      }
                    />
                    <Route
                      path="/accept-invite/:token"
                      element={<AcceptInvitePage />}
                    />

                    {/* Protected Routes - require authenticated user (use MainLayout internally) */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/change-password"
                      element={
                        <ProtectedRoute>
                          <ChangePasswordPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Dashboard Routes. Each guard does what its name says (auth/RouteGuards.js):
                        ProtectedRoute = any signed-in colleague; AdminRoute = administrators;
                        ToolRoute = a tool's page, refused to a standard user when the tool is
                        switched off for standard users in Settings. Every route and who it lets
                        in is listed in src/auth/__tests__/routes.test.js: a new route fails there
                        until it is added. */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/map"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout fullBleed>
                            <FullMap embedded />
                          </DashboardLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/users"
                      element={
                        <AdminRoute>
                          <UserManagementPage />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/dashboard/invite"
                      element={
                        <AdminRoute>
                          <InviteUserPage />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/dashboard/config"
                      element={
                        <AdminRoute>
                          <ConfigPage />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings"
                      element={
                        <AdminRoute>
                          <SettingsPage />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/dashboard/backup"
                      element={
                        <AdminRoute>
                          <BackupPage />
                        </AdminRoute>
                      }
                    />

                    {/* Dashboard Tools Routes */}
                    <Route
                      path="/dashboard/tools/redirects"
                      element={
                        <ToolRoute toolId="redirects">
                          <RedirectsPage />
                        </ToolRoute>
                      }
                    />
                    {/* Old per-tool URLs now land on the matching tab of the
                        combined Promoters tool. */}
                    <Route
                      path="/dashboard/tools/banner-ads"
                      element={
                        <Navigate to="/dashboard/tools/promoters?tab=banners" replace />
                      }
                    />
                    <Route
                      path="/dashboard/tools/banner-ads/:referralKey"
                      element={
                        <ToolRoute toolId="promoters">
                          <BannerAdDetailPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/promoters"
                      element={
                        <ToolRoute toolId="promoters">
                          <PromotersPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/promoter-visits/:referralKey"
                      element={
                        <ToolRoute toolId="promoters">
                          <PromoterVisitDetailPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/orders"
                      element={
                        <Navigate to="/dashboard/tools/promoters?tab=orders" replace />
                      }
                    />
                    <Route
                      path="/dashboard/tools/social"
                      element={
                        <ToolRoute toolId="social">
                          <SocialPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/health"
                      element={
                        <AdminRoute>
                          <HealthPage />
                        </AdminRoute>
                      }
                    />
                    {AnalyticsPreview && (
                      <Route path="/dev/analytics/:page?" element={<AnalyticsPreview />} />
                    )}
                    {SupportSettingsPreview && (
                      <Route path="/dev/support-settings" element={<SupportSettingsPreview />} />
                    )}
                    {/* Analytics (beta): each page is a real route, so Back and shared links work. */}
                    <Route
                      path="/dashboard/tools/analytics"
                      element={
                        <ToolRoute toolId="analytics">
                          <AnalyticsPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/analytics/:page"
                      element={
                        <ToolRoute toolId="analytics">
                          <AnalyticsPage />
                        </ToolRoute>
                      }
                    />
                    {/* Support: the Live view (and, for administrators, the relay's Settings). Its switch
                        starts OFF for standard users (SupportTool.switchedOnByDefault). /support is the
                        same Live view filling the window, to bookmark, pop out or add to the Dock. */}
                    <Route
                      path="/dashboard/tools/support"
                      element={
                        <ToolRoute toolId="support">
                          <SupportPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/support"
                      element={
                        <ToolRoute toolId="support">
                          <SupportFullScreen />
                        </ToolRoute>
                      }
                    />
                    {/* Campaigns (beta): each campaign is a real route, so Back and shared links work. */}
                    <Route
                      path="/dashboard/tools/campaigns"
                      element={
                        <ToolRoute toolId="campaigns">
                          <CampaignsPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/campaigns/:campaign"
                      element={
                        <ToolRoute toolId="campaigns">
                          <CampaignsPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/engagement"
                      element={
                        <ToolRoute toolId="engagement">
                          <EngagementPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tools/engagement/:sessionId"
                      element={
                        <ToolRoute toolId="engagement">
                          <EngagementDetailPage />
                        </ToolRoute>
                      }
                    />
                    <Route
                      path="/dashboard/properties/*"
                      element={
                        <ToolRoute toolId="properties">
                          <PropertiesPage />
                        </ToolRoute>
                      }
                    />

                    {/* Catch all - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </Suspense>
                </Router>
                </BreadcrumbProvider>
              </FullscreenProvider>
            </OwnerProvider>
          </ToastProvider>
        </AuthProvider>
      </DebugProvider>
    </ErrorBoundary>
  );
};

export default App;
